import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import BrandHeader from "../../components/BrandHeader";
import { ordersApi } from "../../lib/api";
import { ORDER_STATUSES } from "../../lib/constants";

export default function OrdersList() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    ordersApi.list().then((l) => { setOrders(l); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (typeFilter !== "all" && o.orderType !== typeFilter) return false;
      if (q) {
        const name = (o.fullName || "").toLowerCase();
        const num = (o.orderNumber || "").toLowerCase();
        const email = (o.email || "").toLowerCase();
        if (!name.includes(q) && !num.includes(q) && !email.includes(q)) return false;
      }
      return true;
    });
  }, [orders, statusFilter, typeFilter, search]);

  return (
    <div className="min-h-screen" data-testid="orders-list-page">
      <BrandHeader adminMode />
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="ph-overline">Orders</div>
            <h1 className="font-serif text-4xl text-[#2C0A5E] mt-2">All prescriptions</h1>
          </div>
          <Link to="/admin/new-order" className="ph-btn-primary" data-testid="orders-new-order">New Order</Link>
        </div>

        <div className="ph-card p-4 md:p-5 flex flex-wrap gap-3 items-center mb-4">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email or order" className="ph-input max-w-xs" data-testid="orders-search" />
          <select className="ph-input max-w-xs" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="orders-status-filter">
            <option value="all">All statuses</option>
            {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="ml-auto text-sm text-[#6B5E7E]">{filtered.length} of {orders.length}</div>
        </div>

        <div className="ph-card p-0 overflow-hidden">
          {loading ? (
            <p className="p-6 text-[#6B5E7E]">Loading...</p>
          ) : filtered.length === 0 ? (
            <p className="p-6 text-[#6B5E7E]">No orders match your filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="orders-table">
                <thead>
                  <tr className="text-left text-[#6B5E7E] bg-[#FAF8F4]">
                    <th className="py-3 px-4">Order</th>
                    <th className="py-3 px-4">Date</th>
                    <th className="py-3 px-4">Customer</th>
                    <th className="py-3 px-4">Email</th>
                    <th className="py-3 px-4">WhatsApp</th>
                    <th className="py-3 px-4">LP</th>
                    <th className="py-3 px-4">Element</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Address</th>
                    <th className="py-3 px-4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((o) => (
                    <tr key={o.id} className="border-t border-[rgba(106,13,173,0.06)] hover:bg-[#FAF8F4]">
                      <td className="py-3 px-4 font-mono text-[#2C0A5E]">{o.orderNumber}</td>
                      <td className="py-3 px-4 text-[#6B5E7E]">{new Date(o.createdAt).toLocaleDateString("en-IN")}</td>
                      <td className="py-3 px-4 text-[#2C0A5E]">{o.fullName}</td>
                      <td className="py-3 px-4 text-[#6B5E7E] truncate max-w-[180px]">{o.email || "-"}</td>
                      <td className="py-3 px-4 text-[#6B5E7E]">{o.whatsapp}</td>
                      <td className="py-3 px-4 text-[#6A0DAD]">{o.numerology?.lifePath}</td>
                      <td className="py-3 px-4 capitalize text-[#6B5E7E]">{o.dominantElement}</td>
                      <td className="py-3 px-4"><StatusPill value={o.status} /></td>
                      <td className="py-3 px-4"><AddressPill order={o} /></td>
                      <td className="py-3 px-4">
                        <Link to={`/admin/orders/${o.id}`} className="text-[#6A0DAD] hover:underline" data-testid={`order-link-${o.id}`}>Open</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ value }) {
  const map = {
    "Pending Payment": { bg: "#fff3e0", text: "#e65100" },
    "Payment Confirmed": { bg: "#e8f5e9", text: "#1b5e20" },
    "Prescription Sent": { bg: "#e1f5fe", text: "#01579b" },
    "Making": { bg: "#f3e5f5", text: "#4a148c" },
    "Dispatched": { bg: "#e3f2fd", text: "#0d47a1" },
    "Delivered": { bg: "#ede7f6", text: "#311b92" },
  };
  const s = map[value] || { bg: "#eee", text: "#333" };
  return (
    <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: s.bg, color: s.text }}>{value}</span>
  );
}

function AddressPill({ order }) {
  const hasAddr = !!(order.deliveryAddress && order.deliveryAddress.pincode);
  const paid = order.paymentStatus === "Payment Confirmed";
  if (hasAddr) {
    const a = order.deliveryAddress;
    return (
      <span
        className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium"
        title={`${a.line1}, ${a.city}, ${a.state} ${a.pincode}`}
        style={{ backgroundColor: "#e8f5e9", color: "#1b5e20" }}
        data-testid="address-pill-done"
      >
        ✓ {a.city || "Saved"}
      </span>
    );
  }
  if (paid) {
    return (
      <span
        className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium"
        style={{ backgroundColor: "#fff3e0", color: "#e65100" }}
        data-testid="address-pill-pending"
      >
        Address pending
      </span>
    );
  }
  return <span className="text-xs text-[#6B5E7E]" data-testid="address-pill-none">—</span>;
}
